import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Bookmark, Plus, Trash, Pause, Play, LinkSimple, Target } from "@phosphor-icons/react";
import { fmtDate } from "../lib/format";

export default function SavedSearchesPanel({ onChanged }) {
  const [rows, setRows] = useState([]);
  const [url, setUrl] = useState("");
  const [state, setState] = useState("TX");
  const [ptype, setPtype] = useState("Residential");
  const [minDays, setMinDays] = useState(60);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  // Universal parser
  const [parseUrl, setParseUrl] = useState("");
  const [parsing, setParsing] = useState(false);
  // Batch skip-trace
  const [batching, setBatching] = useState(false);
  const [batchLimit, setBatchLimit] = useState(5);

  const load = async () => {
    try { const r = await api.get("/zillow/searches"); setRows(r.data); }
    catch { toast.error("Failed to load saved searches"); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!url.trim()) { toast.error("Paste a Zillow search URL"); return; }
    setBusy(true);
    try {
      await api.post("/zillow/searches", { url: url.trim(), state, property_type: ptype, min_days_on_market: Number(minDays) || 0, label: label.trim() || null });
      toast.success("Saved — hourly crawler will pick it up");
      setUrl(""); setLabel(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this saved search?")) return;
    try { await api.delete(`/zillow/searches/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const parse = async () => {
    if (!parseUrl.trim()) { toast.error("Paste a listing URL"); return; }
    setParsing(true);
    try {
      const r = await api.post("/parse-url", { url: parseUrl.trim() });
      if (r.data.duplicate) toast.warning(`Already on board (${r.data.address || r.data.lead_id})`);
      else if (r.data.parsed) toast.success(`Imported: ${r.data.address} · $${Number(r.data.price).toLocaleString()}`);
      else toast.error(r.data.error || "Could not parse");
      setParseUrl(""); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Parse failed"); }
    finally { setParsing(false); }
  };

  const batchSkipTrace = async () => {
    if (!window.confirm(`Skip-trace top ${batchLimit} hand-picked manual-hold leads? Costs BatchData credits.`)) return;
    setBatching(true);
    try {
      const r = await api.post("/leads/skip-trace-batch", { limit: Number(batchLimit) || 5, sort_by: "projected_profit" });
      const { processed, hits, auto_offers_sent, errors } = r.data;
      toast.success(`Batch: ${hits}/${processed} contacts bought · ${auto_offers_sent} Phase 1 offers fired`);
      if (errors?.length) toast.warning(errors[0].error);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Batch failed"); }
    finally { setBatching(false); }
  };

  return (
    <div className="space-y-4 mb-6">
      <div className="border border-violet-500/40 rounded-sm bg-violet-500/[0.03] p-4" data-testid="paste-url-panel">
        <div className="flex items-start gap-2 mb-3">
          <LinkSimple size={16} className="text-violet-300 mt-0.5" />
          <div>
            <div className="text-sm font-semibold text-violet-200">Paste any listing URL</div>
            <div className="text-[11px] text-zinc-400">Zillow · Homes.com · Redfin — extracts address, price, description, and photo. Skip-trace stays paused.</div>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input value={parseUrl} onChange={e => setParseUrl(e.target.value)} data-testid="paste-url-input"
            placeholder="https://www.zillow.com/homedetails/123-Main-St/..."
            className="flex-1 bg-[#18181b] hairline px-3 py-2 mono text-xs rounded-sm focus:outline-none focus:border-violet-500" />
          <button onClick={parse} disabled={parsing || !parseUrl.trim()} data-testid="paste-url-run"
            className="text-xs mono uppercase tracking-[0.15em] px-3 py-2 rounded-sm bg-violet-500 hover:bg-violet-400 text-white font-semibold flex items-center gap-2 disabled:opacity-50">
            <LinkSimple size={13} weight="bold" /> {parsing ? "Parsing…" : "Import"}
          </button>
        </div>
      </div>

      <div className="border border-amber-500/40 rounded-sm bg-amber-500/[0.03] p-4" data-testid="batch-skip-trace-panel">
        <div className="flex items-start gap-2 mb-3">
          <Target size={16} className="text-amber-300 mt-0.5" />
          <div>
            <div className="text-sm font-semibold text-amber-200">Selective Skip-Trace · top N per day</div>
            <div className="text-[11px] text-zinc-400">Only burns BatchData credits on the N highest-projected-profit manual-hold leads. Auto-fires Phase 1 offer on any hit.</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input type="number" min={1} max={25} value={batchLimit} onChange={e => setBatchLimit(e.target.value)} data-testid="batch-limit"
            className="w-20 bg-[#18181b] hairline px-2 py-2 mono text-xs rounded-sm focus:outline-none focus:border-amber-500" />
          <button onClick={batchSkipTrace} disabled={batching} data-testid="batch-run"
            className="text-xs mono uppercase tracking-[0.15em] px-3 py-2 rounded-sm bg-amber-500 hover:bg-amber-400 text-black font-semibold flex items-center gap-2 disabled:opacity-50">
            <Target size={13} weight="bold" /> {batching ? "Skip-tracing…" : `Skip-trace top ${batchLimit}`}
          </button>
        </div>
      </div>

      <div className="border border-zinc-800 rounded-sm bg-[#0b0b0e]" data-testid="saved-searches-panel">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
          <Bookmark size={14} className="text-emerald-300" />
          <span className="text-sm font-semibold">Saved Zillow searches</span>
          <span className="text-[11px] mono text-zinc-500">· hourly crawl (autonomous mode required)</span>
        </div>
        <form onSubmit={add} className="p-4 grid grid-cols-1 md:grid-cols-6 gap-2 border-b border-zinc-800/70">
          <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://www.zillow.com/homes/fsbo/TX/"
            data-testid="saved-url" className="md:col-span-2 bg-[#18181b] hairline px-2 py-1.5 mono text-xs rounded-sm focus:outline-none focus:border-emerald-500" />
          <select value={state} onChange={e => setState(e.target.value)} data-testid="saved-state"
            className="bg-[#18181b] hairline px-2 py-1.5 mono text-xs rounded-sm">
            {["TX","FL","GA","OH","NC","AZ","TN","SC"].map(s => <option key={s}>{s}</option>)}
          </select>
          <select value={ptype} onChange={e => setPtype(e.target.value)} data-testid="saved-ptype"
            className="bg-[#18181b] hairline px-2 py-1.5 mono text-xs rounded-sm">
            <option>Residential</option><option>Land</option><option>Commercial</option>
          </select>
          <input type="number" value={minDays} onChange={e => setMinDays(e.target.value)} placeholder="Min DOM" data-testid="saved-mindays"
            className="bg-[#18181b] hairline px-2 py-1.5 mono text-xs rounded-sm focus:outline-none focus:border-emerald-500" />
          <button type="submit" disabled={busy} data-testid="saved-add"
            className="text-xs mono uppercase tracking-[0.15em] px-3 py-1.5 rounded-sm bg-emerald-500 hover:bg-emerald-400 text-black font-semibold flex items-center justify-center gap-1 disabled:opacity-50">
            <Plus size={12} weight="bold" /> Save
          </button>
        </form>
        {rows.length === 0 ? (
          <div className="p-6 text-center text-xs text-zinc-500 mono">No saved searches yet. Paste a Zillow FSBO URL above.</div>
        ) : (
          <ul className="divide-y divide-zinc-800/70" data-testid="saved-list">
            {rows.map(r => (
              <li key={r.id} className="p-3 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs font-medium truncate">{r.label || r.url}</div>
                  <div className="text-[10px] mono text-zinc-500 truncate">
                    {r.state} · {r.property_type} · min DOM {r.min_days_on_market}
                    {r.last_run_at ? ` · last ${fmtDate(r.last_run_at)} (${r.last_inserted} new)` : " · not run yet"}
                    {r.last_error ? <span className="text-red-400"> · {r.last_error}</span> : null}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <a href={r.url} target="_blank" rel="noreferrer" className="text-[10px] mono uppercase tracking-[0.15em] px-2 py-1 rounded-sm border border-zinc-700 hover:border-zinc-500">Open</a>
                  <button onClick={() => remove(r.id)} data-testid={`saved-del-${r.id}`} className="text-[10px] mono uppercase tracking-[0.15em] px-2 py-1 rounded-sm border border-red-500/50 text-red-300 hover:bg-red-500/10">
                    <Trash size={11} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
